import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import AuthService from '@/service/AuthService.js'
import { env } from '@/utils/env.js'
import { logSsoLoginSuccess } from '@/utils/ssoAudit.js'
import { equivalentRoles } from '@/utils/planningRoles.js'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const isLoggingOut = ref(false)
  const authInitialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => {
    const hasToken = !!(token.value && token.value.trim())
    const hasUser = !!(user.value && user.value.email)
    const result = hasToken && hasUser
    
    env.debug('Auth state check:', {
      hasToken,
      hasUser,
      isAuthenticated: result,
      tokenLength: token.value?.length || 0,
      userEmail: user.value?.email || 'none'
    })
    
    return result
  })

  const userRoles = computed(() => {
    return user.value?.roles || []
  })

  const userRoleNames = computed(() => {
    if (user.value?.role_names?.length) return user.value.role_names
    return (user.value?.roles || []).map(r => typeof r === 'string' ? r : r?.name).filter(Boolean)
  })

  const userPermissions = computed(() => {
    return user.value?.permissions || []
  })

  const userFullName = computed(() => {
    if (!user.value) return ''
    return user.value.full_name || `${user.value.first_name} ${user.value.last_name}`
  })

  // Actions
  const initializeAuth = async () => {
    // Get stored auth data
    const storedToken = AuthService.getToken()
    const storedUser = AuthService.getUserData()
    
    env.debug('Initializing auth store:', {
      hasStoredToken: !!storedToken,
      hasStoredUser: !!storedUser,
      storedUserEmail: storedUser?.email
    })
    
    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = storedUser

      // Refresh user data from server to ensure roles/permissions are up-to-date
      try {
        const response = await AuthService.getCurrentUser()
        if (response.success) {
          user.value = response.data
          AuthService.updateUserData(response.data)
          env.debug('Auth refreshed from server:', { user: user.value.email })
        }
      } catch (err) {
        env.warn('Could not refresh user from server, using cached data:', err.message)
      }

      env.debug('Auth initialized from storage:', { 
        user: storedUser.email,
        isAuthenticated: isAuthenticated.value 
      })
    } else {
      env.debug('No stored auth data found')
    }
    
    // Debug final state
    env.debug('Auth initialization complete:', {
      hasToken: !!token.value,
      hasUser: !!user.value,
      isAuthenticated: isAuthenticated.value
    })
    authInitialized.value = true
  }

  const login = async (credentials) => {
    loading.value = true
    error.value = null
    
    try {
      env.debug('Attempting login for:', credentials.login)
      const response = await AuthService.login(credentials)

      if (response.success && response.data) {
        const tokenValue = response.data.token
        const userValue = response.data.user

        if (tokenValue && userValue) {
          token.value = tokenValue
          user.value = userValue
          authInitialized.value = true
          
          env.log('Login successful:', { user: user.value?.email || 'unknown' })
          return response
        } else {
          throw new Error('Invalid response: missing token or user data')
        }
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (err) {
      error.value = err.message || 'Login failed'
      env.error('Login error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }


  const logout = async () => {
    // If already logging out, don't try again
    if (isLoggingOut.value) {
      env.debug('Logout already in progress, skipping duplicate call')
      return
    }
    
    isLoggingOut.value = true
    loading.value = true
    let response
    
    try {
      response = await AuthService.logout()
    } catch (err) {
      env.warn('Logout API call failed:', err.message)
    } finally {
      // Clear local state regardless of API response
      token.value = null
      user.value = null
      error.value = null
      loading.value = false
      isLoggingOut.value = false
      authInitialized.value = false
      
      env.log('User logged out')
    }

    return response
  }

  const getCurrentUser = async () => {
    if (!token.value) {
      throw new Error('No authentication token')
    }
    
    loading.value = true
    
    try {
      const response = await AuthService.getCurrentUser()
      
      if (response.success) {
        user.value = response.data
        // Update stored user data
        AuthService.updateUserData(response.data)
        env.debug('Current user updated:', { user: user.value.email })
        return response
      } else {
        throw new Error(response.message || 'Failed to get current user')
      }
    } catch (err) {
      env.error('Get current user error:', err)
      // If token is invalid, clear auth data
      if (err.message?.includes('Unauthorized') || err.status === 401) {
        await logout()
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const loginWithToken = async (rawToken) => {
    loading.value = true
    error.value = null

    try {
      AuthService.setAuthData(rawToken, {})
      token.value = rawToken

      const response = await AuthService.getCurrentUser()
      if (response.success && response.data) {
        user.value = response.data
        AuthService.setAuthData(rawToken, response.data)
        authInitialized.value = true
        logSsoLoginSuccess(user.value, 'backend_redirect')
        env.log('SSO login successful:', user.value.email)
        return response
      }
      throw new Error('Failed to fetch user after SSO login')
    } catch (err) {
      AuthService.clearAuthData()
      token.value = null
      user.value = null
      error.value = err.message
      env.error('SSO loginWithToken error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const loginWithKeycloakToken = async (keycloakToken) => {
    loading.value = true
    error.value = null

    try {
      const response = await AuthService.exchangeSsoToken(keycloakToken)
      const appToken = response.data?.token
      const userData = response.data?.user

      if (!response.success || !appToken || !userData) {
        throw new Error(response.message || 'Failed to exchange SSO token')
      }

      token.value = appToken
      user.value = userData
      authInitialized.value = true
      logSsoLoginSuccess(user.value, 'public_client_exchange')
      return response
    } catch (err) {
      AuthService.clearAuthData()
      token.value = null
      user.value = null
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await AuthService.changePassword(passwordData)
      env.log('Password changed successfully')
      return response
    } catch (err) {
      error.value = err.message || 'Failed to change password'
      env.error('Change password error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (profileData) => {
    loading.value = true
    error.value = null

    try {
      const response = await AuthService.updateProfile(profileData)
      if (response.success && response.data) {
        user.value = response.data
        AuthService.updateUserData(response.data)
        env.log('Profile updated successfully')
      }
      return response
    } catch (err) {
      error.value = err.message || 'Failed to update profile'
      env.error('Update profile error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const uploadAvatar = async (file) => {
    loading.value = true
    error.value = null

    try {
      const response = await AuthService.uploadAvatar(file)
      if (response.success && response.data) {
        user.value = response.data
        AuthService.updateUserData(response.data)
        env.log('Avatar uploaded successfully')
      }
      return response
    } catch (err) {
      error.value = err.message || 'Failed to upload avatar'
      env.error('Upload avatar error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const removeAvatar = async () => {
    loading.value = true
    error.value = null

    try {
      const response = await AuthService.removeAvatar()
      if (response.success && response.data) {
        user.value = response.data
        AuthService.updateUserData(response.data)
        env.log('Avatar removed successfully')
      }
      return response
    } catch (err) {
      error.value = err.message || 'Failed to remove avatar'
      env.error('Remove avatar error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const hasRole = (role) => {
    // Several planning roles rename a role the system already had, so a check for either
    // name accepts the other. See src/utils/planningRoles.js.
    return equivalentRoles(role).some(name => holdsExactRole(name))
  }

  const holdsExactRole = (role) => {
    // Check role_names array (simple string array from backend)
    if (user.value?.role_names?.includes(role)) return true
    // Check from roles array (objects with .name or plain strings)
    const inRolesArray = userRoles.value.some(userRole => {
      if (typeof userRole === 'string') return userRole === role
      return userRole.name === role || userRole.id === role
    })
    if (inRolesArray) return true
    // Fallback: check user.role / user.role_name string fields
    if (user.value?.role === role || user.value?.role_name === role) return true
    return false
  }

  // Super Admin has every permission (mirrors User::isUnrestrictedAdmin on the backend).
  const isSuperAdmin = () => hasRole('Super Admin')

  const hasPermission = (permission) => {
    if (isSuperAdmin()) return true
    return userPermissions.value.some(userPermission => {
      // Handle object form { id, name, category } (from backend)
      if (typeof userPermission === 'object' && userPermission !== null) {
        return userPermission.name === permission || userPermission.id === permission
      }
      // Handle plain string form
      return userPermission === permission
    })
  }

  const hasAnyRole = (roles) => {
    return roles.some(role => hasRole(role))
  }

  const hasAllRoles = (roles) => {
    return roles.every(role => hasRole(role))
  }

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission))
  }

  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission))
  }

  const clearError = () => {
    error.value = null
  }

  const updateUserData = (userData) => {
    user.value = { ...user.value, ...userData }
    AuthService.updateUserData(user.value)
    env.debug('User data updated:', user.value)
  }

  // Listen for auth events from API interceptors
  if (typeof window !== 'undefined') {
    window.addEventListener('auth:logout', (event) => {
      env.debug('Auth logout event received', event.detail)
      
      // Only handle automatic logout if not already in logout process
      if (!isLoggingOut.value) {
        // If the event was from the API interceptor, just clear the state without calling API again
        if (event.detail?.reason === 'unauthorized') {
          env.debug('Unauthorized detected, clearing auth state')
          // Clear local state without calling logout API again
          token.value = null
          user.value = null
          error.value = null
          env.log('User logged out due to unauthorized access')
        } else if (event.detail?.source === 'authService') {
          // Manual logout from AuthService, just clear local state
          token.value = null
          user.value = null
          error.value = null
        }
      }
    })
    
    window.addEventListener('auth:login', (event) => {
      env.debug('Auth login event received:', event.detail)
      if (event.detail?.user) {
        user.value = event.detail.user
      }
    })
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    authInitialized,
    
    // Getters
    isAuthenticated,
    userRoles,
    userRoleNames,
    userPermissions,
    userFullName,
    
    // Actions
    initializeAuth,
    login,
    loginWithToken,
    loginWithKeycloakToken,
    logout,
    getCurrentUser,
    changePassword,
    updateProfile,
    uploadAvatar,
    removeAvatar,
    hasRole,
    hasPermission,
    hasAnyRole,
    hasAllRoles,
    hasAnyPermission,
    hasAllPermissions,
    clearError,
    updateUserData
  }
})
